import qs from "query-string";

import { getProPageHeaders } from "@similarweb/pro-logic-components";
import Sentry from "@similarweb/sentry-logger";

import { EXCEL_DOWNLOAD_LINK } from "app/pages/activation/components/Topics/constants";
import type {
    DownloadExcelBody,
    ShortTopicSchema,
    SuggestionSchema,
    TopicSchema,
    TopicWithKeywordsSchema,
} from "app/pages/activation/components/Topics/types";
import { ACTIVATION_API } from "app/pages/activation/services/constants";
import type { TopicParams } from "app/pages/activation/types/api";
import type { IFetchService } from "app/services/fetchService";

type TopicsApiServiceSchema = {
    get(params: TopicParams): Promise<TopicSchema[]>;
    getSuggestions(activationObjectId: string): Promise<SuggestionSchema[] | null>;
    getById(activationObjectId: string, topicId: string): Promise<ShortTopicSchema | null>;
    create(activationObjectId: string, topics: string[]): Promise<ShortTopicSchema[]>;
    createWithKW(activationObjectId: string, topics: TopicWithKeywordsSchema[]): Promise<ShortTopicSchema[]>;
    createFromExistingKW(activationObjectId: string, keywordsIds: string[]): Promise<ShortTopicSchema[]>;
    delete(activationObjectId: string, topicsId: string[], shouldRemoveKWList: boolean): Promise<void>;
    update(params: { activationId: string; topic: ShortTopicSchema }): Promise<ShortTopicSchema>;
    downloadExcel(body: DownloadExcelBody): Promise<{ blob: Blob; filename: string }>;
};

export const topicsApiService = (fetchService: IFetchService): TopicsApiServiceSchema => ({
    get: async ({ activationId, ...restParams }: TopicParams) => {
        try {
            return await fetchService.get(`${ACTIVATION_API}/${activationId}/topics/metrics`, restParams);
        } catch (error) {
            Sentry.error(`Error fetching activation Topics -`, error);
            throw new Error("Failed to fetch Topics");
        }
    },
    getSuggestions: async (activationObjectId: string) => {
        try {
            return await fetchService.get(`${ACTIVATION_API}/${activationObjectId}/topics/suggestions`);
        } catch (error) {
            Sentry.error(`Error fetching activation Topic Suggestions -`, error);
            throw new Error("Failed to fetch Topic Suggestions");
        }
    },
    create: async (activationObjectId: string, topics: string[]) => {
        try {
            return await fetchService.post(`${ACTIVATION_API}/${activationObjectId}/topics`, topics);
        } catch (error) {
            Sentry.error(`Error creating Topics -`, error);
            throw new Error("Failed to create Topics");
        }
    },
    createWithKW: async (activationObjectId: string, topics: TopicWithKeywordsSchema[]) => {
        try {
            return await fetchService.post(`${ACTIVATION_API}/${activationObjectId}/topics/from-keywords`, topics);
        } catch (error) {
            Sentry.error(`Error creating Topics -`, error);
            throw new Error("Failed to create Topic with Keywords");
        }
    },
    createFromExistingKW: async (activationObjectId: string, keywordsIds: string[]) => {
        try {
            return await fetchService.post(
                `${ACTIVATION_API}/${activationObjectId}/topics/from-existing-groups`,
                keywordsIds,
            );
        } catch (error) {
            Sentry.error(`Error creating Topics from existing KW groups -`, error);
            throw new Error("Failed to create Topic with existing KW groups");
        }
    },
    delete: async (activationObjectId: string, topicsId: string[], shouldRemoveKWList: boolean) => {
        try {
            return await fetchService.delete(
                `${ACTIVATION_API}/${activationObjectId}/topics`,
                { deleteRelatedKeywordGroups: shouldRemoveKWList },
                topicsId,
            );
        } catch (error) {
            Sentry.error(`Error deleting Topics -`, error);
            throw new Error("Failed to delete Topics");
        }
    },
    getById: async (activationObjectId: string, topicId: string) => {
        try {
            return await fetchService.get(`${ACTIVATION_API}/${activationObjectId}/topics/${topicId}`);
        } catch (error) {
            Sentry.error(`Error fetching activation Topic by Id -`, error);
            throw new Error("Failed to fetch Topic by Id");
        }
    },
    update: async ({ activationId, topic }) => {
        try {
            return await fetchService.put(`${ACTIVATION_API}/${activationId}/topics`, topic);
        } catch (error) {
            Sentry.errorTracker({
                message: "topicsApiService: Error updating Topic",
                e: error,
                level: "error",
                params: {
                    extra: {
                        ...(topic || {}),
                    },
                },
            });
            throw new Error("Failed to update Topic");
        }
    },
    downloadExcel: async (body) => {
        const qParams = {
            asc: false,
            sort: "volume",
        };

        const response = await fetch(`${EXCEL_DOWNLOAD_LINK}?${qs.stringify(qParams)}`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                ...getProPageHeaders(),
            },
            body: JSON.stringify(body),
        });

        if (!response.ok) {
            const errorInfo = await response.json().catch(() => ({ message: "Error downloading Topics Excel" }));
            Sentry.error(`Error downloading Topics Excel`, errorInfo);
            throw new Error(`Failed to download Excel: ${response.status} ${response.statusText}`);
        }

        const filename = response.headers.get("Content-Disposition")?.split("filename=")[1] || "keywords_data.xlsx";

        const blob = await response.blob();
        return { blob, filename };
    },
});
